import * as React from 'react';
import { isEqual } from 'lodash-es';
import { useNavigate } from 'react-router';
import { useSelector } from "react-redux";
import { Tooltip } from 'primereact/tooltip';

import { ApplicationState } from '../../../store';
import { constantTable, getConstantQueryFetchPaged } from '../../../helpers/constants';
import { DataTablePagedList } from '../../../components/datatables/DataTablePagedList';
import { PagedList } from '../../../types/dataQueries/base/PagedList';
import { ProjectDoc } from '../models/ProjectDoc';
import { projectDocColumns } from '../models/ProjectDocColumns';
import { ProjectDocQuery } from '../models/ProjectDocQuery';
import { services } from '../../../services';
import { useBackgroundPromiseEffectByCondition } from '../../../helpers/hooks/useBackgroundPromiseEffectByCondition';
import { TableCard } from '../../../components/synced/ui/TableCard/TableCard';
import { docsPaginatorTemplate } from '../../../components/synced/ui/docsPaginatorTemplate/docsPaginatorTemplate';
import { useIsMobile } from '../../../helpers/hooks/useIsMobile';
import { ProjectDocsMobileList } from './mobile/ProjectDocsMobileList';
import { projectDocQueryToTableFilters } from '../helpers/projectDocQueryToTableFilters';

const FILTERS_STORAGE_KEY = `ProjectDocsFilters`;

const persistFilters = (q: ProjectDocQuery) => {
  const { TitleContains, ProjectTitle, DocTypeId, StatusIds, CreatedQueryParams, Sorts, Skip, Take } = q;
  sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({ TitleContains, ProjectTitle, DocTypeId, StatusIds, CreatedQueryParams, Sorts, Skip, Take }));
};

export const ProjectDocsList: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile(768);

  const contractorIds = useSelector(
    (appState: ApplicationState) => appState.context.userContext.Contractors.map(c => c.Id ?? 0),
    isEqual,
  );
  const [dataQuery, setDataQuery] = React.useState<ProjectDocQuery>(() => {
    const saved = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    const restored = saved ? JSON.parse(saved) : {};
    return {
      ...getConstantQueryFetchPaged(),
      StatusIds: [],
      ...restored,
      ContractorIds: contractorIds,
      IsDeleted: false,
      ProjectIsDeleted: false,
    };
  });

  const [data, setData] = React.useState<PagedList<ProjectDoc>>({ ...constantTable.initialPagedList });
  const [queryIsInited, setQueryIsInited] = React.useState(isMobile);

  const doctypeTooltipRef = React.useRef<Tooltip>(null);
  React.useEffect(() => {
    doctypeTooltipRef.current?.updateTargetEvents();
  }, [data]);

  const isLoading = useBackgroundPromiseEffectByCondition(
    () => queryIsInited,
    () => services.projectDocService.fetchPaged(dataQuery),
    (res: PagedList<ProjectDoc>) => setData(res),
    [dataQuery, queryIsInited]
	);

  const updateQuery = React.useCallback((q: ProjectDocQuery) => {
    setQueryIsInited(true);
    const newQ = { ...dataQuery, ...q, IsDeleted: false, ProjectIsDeleted: false };
    if (!isEqual(dataQuery, newQ)) {
        setDataQuery(newQ);
        persistFilters(newQ);
    }
  }, [dataQuery]);


  const goToDoc = React.useCallback((doc: ProjectDoc) => navigate(`/projectdocs/${doc.Id}`), [navigate]);

  const onSelectionChange = (e) => goToDoc(e.value as ProjectDoc);


  return (
    <div className="projectdocs-list-page">
      {isMobile ? (
        <ProjectDocsMobileList
          data={data}
          isLoading={isLoading}
          initialQuery={dataQuery}
          updateQuery={updateQuery}
          onSelect={goToDoc} />
      ) : (
        <TableCard>
          <Tooltip
            ref={doctypeTooltipRef}
            target=".doctype-pill-tooltip-target"
            className="doctype-pill-tooltip"
            position="top"
            showDelay={200} />
          <DataTablePagedList
            loading={isLoading}
            data={data}
            allColumns={projectDocColumns}
            tableName={`ProjectDocsList`}
            updateQuery={updateQuery}
            externalFilters={projectDocQueryToTableFilters(dataQuery)}
            selectionMode="single"
            onSelectionChange={onSelectionChange}
            paginatorTemplate={docsPaginatorTemplate}
            filterDisplay="row" />
        </TableCard>
      )}
    </div>
  );
}
